/**
 * Client-side spell assembly, used by the tutorial.
 *
 * During a match the server builds and owns every spell. The tutorial runs
 * before the player has joined anything, so it composes the parry spell here
 * and stores it locally — but it asks the server for the interpretation when it
 * can, so a first-time player still gets the real AI reading. When the player
 * later joins a room, the server re-prices whatever it receives from storage,
 * so nothing built here can be tampered with into an advantage.
 */
import {
  BALANCE_RULES_VERSION,
  SPELL_SCHEMA_VERSION,
  type GesturePoint,
} from '@spell/shared-types';
import { balanceFromInterpretation } from '@spell/combat-rules';
import { buildTemplate } from '@spell/gesture-engine';
import { interpretLocally } from '@spell/ai-interpreter/local';
import { normalizeChant, spellSchema, type Spell, type SpellInterpretation } from '@spell/spell-schema';
import { serverHttpUrl } from '../networking/net';

export interface BuildSpellInput {
  type: 'parry' | 'signature';
  chant: string;
  description: string;
  gestureSamples: GesturePoint[][];
  ownerId: string;
  id: string;
}

export interface BuiltSpell {
  spell: Spell;
  notes: string[];
  interpreter: 'ai' | 'local';
}

async function fetchInterpretation(
  type: 'parry' | 'signature',
  chant: string,
  description: string,
): Promise<{ interpretation: SpellInterpretation; source: 'ai' | 'local'; note?: string }> {
  try {
    const res = await fetch(`${serverHttpUrl()}/api/interpret`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ type, chant, description }),
    });
    if (!res.ok) throw new Error(String(res.status));
    const data = (await res.json()) as {
      interpreter: 'ai' | 'local';
      summary: string;
      publicSummary: string;
      category: SpellInterpretation['category'];
      element: SpellInterpretation['element'];
      projectileShape: SpellInterpretation['projectileShape'];
      color: string;
      accentColor: string;
      vfxTags: SpellInterpretation['vfxTags'];
      circleComplexity: number;
      effects: SpellInterpretation['effects'];
      unconditional?: boolean;
      volley?: boolean;
    };
    return {
      source: data.interpreter,
      interpretation: {
        category: data.category,
        element: data.element,
        projectileShape: data.projectileShape,
        color: data.color,
        accentColor: data.accentColor,
        vfxTags: data.vfxTags,
        circleComplexity: data.circleComplexity,
        effects: data.effects,
        summary: data.summary,
        publicSummary: data.publicSummary,
        // The shape flags decide how the same effect list is priced, so they
        // have to survive the round trip — a spell rebuilt without them would
        // not match the numbers the player just confirmed on screen.
        unconditional: data.unconditional ?? false,
        volley: data.volley ?? false,
      },
    };
  } catch {
    return {
      source: 'local',
      interpretation: interpretLocally({ type, chant, description }),
      note: '서버에 연결할 수 없어 로컬 해석기를 사용했습니다.',
    };
  }
}

export async function buildSpellLocally(input: BuildSpellInput): Promise<BuiltSpell> {
  const normalized = normalizeChant(input.chant) || 'SPELL';
  const { interpretation, source, note } = await fetchInterpretation(
    input.type,
    input.chant,
    input.description,
  );
  const balanced = balanceFromInterpretation(input.type, normalized, interpretation);
  const template = buildTemplate(input.gestureSamples);

  const spell: Spell = {
    schemaVersion: SPELL_SCHEMA_VERSION,
    id: input.id,
    ownerId: input.ownerId,
    type: input.type,
    category: interpretation.category,
    chant: input.chant.slice(0, 48),
    normalizedChant: normalized,
    description: input.description.slice(0, 400),
    summary: interpretation.summary,
    publicSummary: interpretation.publicSummary,
    gesture: template.strokeCount > 0 ? template : null,
    effects: balanced.effects,
    visuals: {
      color: interpretation.color,
      accentColor: interpretation.accentColor,
      element: interpretation.element,
      projectileShape: interpretation.projectileShape,
      vfxTags: interpretation.vfxTags,
      circleComplexity: interpretation.circleComplexity,
    },
    stats: balanced.stats,
    balanceVersion: BALANCE_RULES_VERSION,
    interpreter: source,
    createdAt: Date.now(),
  };

  // Validate before it ever reaches storage: a spell that fails the schema now
  // would be silently dropped on the next boot instead.
  const parsed = spellSchema.safeParse(spell);
  if (!parsed.success) {
    throw new Error(`주문 데이터 검증 실패: ${parsed.error.issues[0]?.message ?? 'unknown'}`);
  }

  const notes = [...balanced.notes];
  if (note) notes.push(note);
  return { spell: parsed.data, notes, interpreter: source };
}
